import React, { useState, useEffect, useMemo, useRef } from "react";
import axiosInstance from "../../axiosInstance";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./payrollComp.scss";
import IconButton from "../../components/IconButton/IconButton";
import ColoredCheckbox from "../../components/ColoredCheckbox/ColoredCheckbox";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";

function PayrollComp() {
  const [payrollData, setPayrollData] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const actionBarRef = useRef(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Months array for selection
  const months = [
    { number: 1, name: "January", emoji: "❄️" },
    { number: 2, name: "February", emoji: "💝" },
    { number: 3, name: "March", emoji: "🌸" },
    { number: 4, name: "April", emoji: "🌷" },
    { number: 5, name: "May", emoji: "🌺" },
    { number: 6, name: "June", emoji: "☀️" },
    { number: 7, name: "July", emoji: "🌞" },
    { number: 8, name: "August", emoji: "🌻" },
    { number: 9, name: "September", emoji: "🍂" },
    { number: 10, name: "October", emoji: "🎃" },
    { number: 11, name: "November", emoji: "🦃" },
    { number: 12, name: "December", emoji: "🎄" },
  ];

  // Set default month selection to current month
  useEffect(() => {
    const currentMonth = new Date().getMonth() + 1;
    const currentMonthObj = months.find(
      (month) => month.number === currentMonth
    );
    if (currentMonthObj) {
      setSelectedMonth(currentMonthObj);
      fetchPayrollData(currentMonthObj.number, selectedYear);
    }
  }, []);

  useEffect(() => {
    if (selectedMonth) {
      fetchPayrollData(selectedMonth.number, selectedYear);
    }
  }, [selectedYear]);

  const fetchPayrollData = async (month, yearParam) => {
    setLoading(true);
    setError(null);
    setPayrollData([]);
    setSelectedIds([]);

    // Use parameter if provided, else fall back to current selectedYear.
    const year = yearParam || selectedYear;
    const dateString = `${year}-${month.toString().padStart(2, "0")}-01`;

    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_HR_URL}/payroll-history/requested`,
        dateString,
        {
          headers: { "Content-Type": "application/json" },
        }
      );
      if (response.data && response.data.data) {
        setPayrollData(response.data.data);
      } else {
        setError("No payroll data found for the selected month");
      }
    } catch (error) {
      setError("Error fetching payroll data. Please try again.");
      toast.error("Error fetching payroll data.");
    } finally {
      setLoading(false);
    }
  };

  const handleMonthSelection = (month) => {
    setSelectedMonth(month);
    fetchPayrollData(month.number, selectedYear);
  };

  // Auto-scroll to action bar when selections are made
  useEffect(() => {
    if (selectedIds.length > 0 && actionBarRef.current) {
      // Smooth scroll to action bar
      actionBarRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });

      // Optional: Show a toast notification
      if (selectedIds.length === payrollData.length && payrollData.length > 1) {
        toast.info(
          `All ${selectedIds.length} records selected. Scroll down to see bulk actions.`,
          {
            position: "top-center",
            autoClose: 3000,
          }
        );
      } else if (selectedIds.length === 1) {
        toast.info("1 record selected. Scroll down to see bulk actions.", {
          position: "top-center",
          autoClose: 2000,
        });
      }
    }
  }, [selectedIds.length, payrollData.length]);

  const handleSelect = (id) => {
    const historyId = payrollData[id]?.employeePayrollHistoryId;
    if (!historyId) return;

    setSelectedIds((prevSelected) =>
      prevSelected.includes(historyId)
        ? prevSelected.filter((item) => item !== historyId)
        : [...prevSelected, historyId]
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedIds(
      checked ? payrollData.map((item) => item.employeePayrollHistoryId) : []
    );
  };

  const handleBulkAction = (action) => {
    if (selectedIds.length === 0) {
      toast.warning("No records selected for action.");
      return;
    }

    const endpoint =
      action === "Approve"
        ? `${process.env.REACT_APP_API_HR_URL}/payroll-history/approve`
        : `${process.env.REACT_APP_API_HR_URL}/payroll-history/reject`;

    axiosInstance
      .post(endpoint, selectedIds)
      .then(() => {
        toast.success(`${action} successful for selected records.`);
        // Only call fetchPayrollData once with the correct parameter
        if (selectedMonth) {
          fetchPayrollData(selectedMonth.number);
        }
      })
      .catch((error) => {
        toast.error(`Failed to ${action.toLowerCase()} selected records.`);
        console.error(`Error in ${action.toLowerCase()}ing records:`, error);
      });
  };

  const handleIndividualAction = (action, historyId) => {
    const endpoint =
      action === "Approve"
        ? `${process.env.REACT_APP_API_HR_URL}/payroll-history/approve`
        : `${process.env.REACT_APP_API_HR_URL}/payroll-history/reject`;

    axiosInstance
      .post(endpoint, [historyId])
      .then(() => {
        toast.success(`Record ${action.toLowerCase()}ed successfully.`);
        // Remove the record from payrollData by filtering based on employeePayrollHistoryId
        setPayrollData((prevData) =>
          prevData.filter((item) => item.employeePayrollHistoryId !== historyId)
        );
        // Also remove from selectedIds if it was selected
        setSelectedIds((prevSelected) =>
          prevSelected.filter((id) => id !== historyId)
        );
      })
      .catch((error) => {
        toast.error(`Error ${action.toLowerCase()}ing record.`);
        console.error(`Error in ${action.toLowerCase()}ing record:`, error);
      });
  };

  // Calculate summary statistics
  const calculateTotals = () => {
    return payrollData.reduce(
      (acc, employee) => {
        acc.totalFixedGross += employee.fixedGrossSalary || 0;
        acc.totalEarnedGross += employee.earnedGrossSalary || 0;
        acc.totalDeductions += Math.abs(employee.totalDeductions || 0);
        acc.totalNetPay += employee.netPay || 0;
        // Add new totals
        acc.totalEarnedOtherAllowances += employee.earnedOtherAllowances || 0;
        return acc;
      },
      {
        totalFixedGross: 0,
        totalEarnedGross: 0,
        totalDeductions: 0,
        totalNetPay: 0,
        totalEarnedOtherAllowances: 0,
      }
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Get unique categories for dynamic columns
  const tableStructure = useMemo(() => {
    if (payrollData.length === 0) return null;

    // Get unique fixed earnings
    const fixedEarningsNames = payrollData.flatMap((item) =>
      (item.fixedEarnings || []).map(
        (earning) => earning.salaryName || earning.name
      )
    );
    const uniqueFixedEarnings = [...new Set(fixedEarningsNames)];

    // Get unique earned earnings
    const earnedEarningsNames = payrollData.flatMap((item) =>
      (item.earnedEarnings || []).map(
        (earning) => earning.salaryName || earning.name
      )
    );
    const uniqueEarnedEarnings = [...new Set(earnedEarningsNames)];

    // Get unique allowances
    const allowanceNames = payrollData.flatMap((item) =>
      (item.allowances || []).map(
        (allowance) => allowance.salaryName || allowance.name
      )
    );
    const uniqueAllowances = [...new Set(allowanceNames)];

    // Get unique reimbursements
    const reimbursementNames = payrollData.flatMap((item) =>
      (item.reimbursement || []).map((reimb) => reimb.salaryName || reimb.name)
    );
    const uniqueReimbursements = [...new Set(reimbursementNames)];

    // Get unique loans
    const loanNames = payrollData.flatMap((item) =>
      (item.loans || []).map((loan) => loan.salaryName || loan.name)
    );
    const uniqueLoans = [...new Set(loanNames)];

    // Get unique employee contributions
    const employeeContribNames = payrollData.flatMap((item) =>
      (item.employeeContribution || []).map(
        (contrib) => contrib.salaryName || contrib.name
      )
    );
    const uniqueEmployeeContribs = [...new Set(employeeContribNames)];

    // Get unique deductions
    const deductionNames = payrollData.flatMap((item) =>
      (item.otherDeductions || []).map(
        (deduction) => deduction.salaryName || deduction.name
      )
    );

    const uniqueDeductions = [...new Set(deductionNames)];

    // Get unique employer contributions
    const employerContribNames = payrollData.flatMap((item) =>
      (item.employerContribution || []).map(
        (contrib) => contrib.salaryName || contrib.name
      )
    );
    const uniqueEmployerContribs = [...new Set(employerContribNames)];

    return {
      uniqueFixedEarnings,
      uniqueEarnedEarnings,
      uniqueAllowances,
      uniqueReimbursements,
      uniqueLoans,
      uniqueEmployeeContribs,
      uniqueDeductions,
      uniqueEmployerContribs,
    };
  }, [payrollData]);

  // Transform data for table
  const transformedData = payrollData.map((item, index) => {
    const baseData = {
      employeeWithSelect: (
        <div className="employee-select-column">
          <ColoredCheckbox
            label=""
            color="green"
            checked={selectedIds.includes(item.employeePayrollHistoryId)}
            onChange={() => handleSelect(index)}
            name={`checkbox-${index}`}
          />
          <div className="employee-info">
            <div className="employee-name">{item.accountHolderName}</div>
          </div>
        </div>
      ),
      userEmployeeId: item.userEmployeeId,
      period: item.period,
      paidDays: item.paidDays,
      baseDays: item.baseDays,
      lossOfPay: (
        <span className="currency-value loss-of-pay">
          {item.lossOfPay?.toLocaleString() || 0}
        </span>
      ),
      fixedGrossSalary: (
        <span className="currency-value earnings-value">
          ₹{item.fixedGrossSalary?.toLocaleString() || 0}
        </span>
      ),
      earnedGrossSalary: (
        <span className="currency-value earnings-value">
          ₹{item.earnedGrossSalary?.toLocaleString() || 0}
        </span>
      ),
      fixedNetSalary: (
        <span className="currency-value earnings-value">
          ₹{item.fixedNetSalary?.toLocaleString() || 0}
        </span>
      ),
      // Total Earned Other Allowance column
      totalEarnedOtherAllowances: (
        <span className="currency-value allowance-value">
          ₹{(item.earnedOtherAllowances || 0).toLocaleString()}
        </span>
      ),
      totalDeductions: (
        <span className="currency-value deduction-value">
          ₹{Math.abs(item.totalDeductions || 0).toLocaleString()}
        </span>
      ),
      netPay: (
        <span className="currency-value net-pay">
          ₹{item.netPay?.toLocaleString() || 0}
        </span>
      ),
      actions: (
        <div className="action-buttons">
          <IconButton
            variant="add"
            onClick={() =>
              handleIndividualAction("Approve", item.employeePayrollHistoryId)
            }
            icon="mdi:tick"
          />
          <IconButton
            variant="delete"
            onClick={() =>
              handleIndividualAction("Reject", item.employeePayrollHistoryId)
            }
            icon="mdi:close"
          />
        </div>
      ),
    };

    // Add dynamic columns based on table structure
    if (tableStructure) {
      // Fixed Earnings
      tableStructure.uniqueFixedEarnings.forEach((earningName) => {
        const earning = (item.fixedEarnings || []).find(
          (e) => (e.salaryName || e.name) === earningName
        );
        baseData[`fixedEarning_${earningName}`] = (
          <span className="currency-value earnings-value">
            ₹{(earning?.value || earning?.amount || 0).toLocaleString()}
          </span>
        );
      });

      // Earned Earnings
      tableStructure.uniqueEarnedEarnings.forEach((earningName) => {
        const earning = (item.earnedEarnings || []).find(
          (e) => (e.salaryName || e.name) === earningName
        );
        baseData[`earnedEarning_${earningName}`] = (
          <span className="currency-value earnings-value">
            ₹{(earning?.value || earning?.amount || 0).toLocaleString()}
          </span>
        );
      });

      // Allowances
      tableStructure.uniqueAllowances.forEach((allowanceName) => {
        const allowance = (item.allowances || []).find(
          (a) => (a.salaryName || a.name) === allowanceName
        );
        baseData[`allowance_${allowanceName}`] = (
          <span className="currency-value allowance-value">
            ₹{(allowance?.value || allowance?.amount || 0).toLocaleString()}
          </span>
        );
      });

      // Reimbursements
      tableStructure.uniqueReimbursements.forEach((reimbName) => {
        const reimb = (item.reimbursement || []).find(
          (r) => (r.salaryName || r.name) === reimbName
        );
        baseData[`reimbursement_${reimbName}`] = (
          <span className="currency-value reimbursement-value">
            ₹{(reimb?.value || reimb?.amount || 0).toLocaleString()}
          </span>
        );
      });

      // Loans
      tableStructure.uniqueLoans.forEach((loanName) => {
        const loan = (item.loans || []).find(
          (l) => (l.salaryName || l.name) === loanName
        );
        baseData[`loan_${loanName}`] = (
          <span className="currency-value deduction-value">
            ₹{(loan?.value || loan?.amount || 0).toLocaleString()}
          </span>
        );
      });

      // Employee Contributions
      tableStructure.uniqueEmployeeContribs.forEach((contribName) => {
        const contrib = (item.employeeContribution || []).find(
          (c) => (c.salaryName || c.name) === contribName
        );
        baseData[`empContrib_${contribName}`] = (
          <span className="currency-value deduction-value">
            ₹{(contrib?.value || contrib?.amount || 0).toLocaleString()}
          </span>
        );
      });

      // Deductions
      tableStructure.uniqueDeductions.forEach((deductionName) => {
        const deduction = (item.otherDeductions || []).find(
          (d) => (d.salaryName || d.name) === deductionName
        );
        baseData[`deduction_${deductionName}`] = (
          <span className="currency-value deduction-value">
            ₹{(deduction?.value || deduction?.amount || 0).toLocaleString()}
          </span>
        );
      });

      // Employer Contributions
      tableStructure.uniqueEmployerContribs.forEach((contribName) => {
        const contrib = (item.employerContribution || []).find(
          (c) => (c.salaryName || c.name) === contribName
        );
        baseData[`emplerContrib_${contribName}`] = (
          <span className="currency-value contribution-value">
            ₹{(contrib?.value || contrib?.amount || 0).toLocaleString()}
          </span>
        );
      });
    }

    return baseData;
  });

  return (
    <div className="payroll-comp">
      <div className="payroll-selection">
        <div className="payroll-selection-header">
          <h1 className="display6">Payroll Management</h1>

          <div className="year-selector">
            <div className="year-arrow-controls">
              <button
                type="button"
                className="year-arrow left"
                aria-label="Previous year"
                disabled={selectedYear <= 1900}
                onClick={() => setSelectedYear((y) => Math.max(1900, y - 1))}
              >
                <svg
                  viewBox="0 0 24 24"
                  className="icon"
                  style={{ verticalAlign: "middle" }}
                >
                  <path d="M15.7 5.7a1 1 0 0 1 0 1.4L10.42 12l5.3 4.9a1 1 0 0 1-1.42 1.4l-6-5.5a1 1 0 0 1 0-1.4l6-5.5a1 1 0 0 1 1.4 0z" />
                </svg>
              </button>
              <input
                id="year-input"
                type="number"
                min={1900}
                max={2100}
                className="year-value"
                value={selectedYear}
                onChange={(e) => {
                  let val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val > 0 && val <= 9999)
                    setSelectedYear(val);
                }}
                placeholder="YYYY"
                style={{ width: 70, textAlign: "center" }}
              />
              <button
                type="button"
                className="year-arrow right"
                aria-label="Next year"
                disabled={selectedYear >= 2100}
                onClick={() => setSelectedYear((y) => Math.min(2100, y + 1))}
              >
                <svg
                  viewBox="0 0 24 24"
                  className="icon"
                  style={{ verticalAlign: "middle" }}
                >
                  <path d="M8.3 18.3a1 1 0 0 1 0-1.4L13.58 12 8.3 7.1a1 1 0 0 1 1.42-1.4l6 5.5a1 1 0 0 1 0 1.4l-6 5.5a1 1 0 0 1-1.4 0z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
        {/* Month Selection Section */}
        <div className="month-selection">
          <div className="month-cards-grid">
            {months.map((month) => (
              <button
                key={month.number}
                onClick={() => handleMonthSelection(month)}
                className={`month-card ${
                  selectedMonth?.number === month.number ? "active" : ""
                }`}
                disabled={loading}
              >
                <div className="month-emoji">{month.emoji}</div>
                <div className="month-name">{month.name}</div>
                <div className="month-year">{selectedYear}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="payroll-comp__error">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {loading && <LoadingScreen />}

      {!loading && !error && payrollData.length === 0 && selectedMonth && (
        <div className="no-payroll-fallback">
          <div className="fallback-icon">📊</div>
          <h3>No Payroll Data Found</h3>
          <p>
            No payroll has been run for {selectedMonth.name}{" "}
            {new Date().getFullYear()}
          </p>
        </div>
      )}

      {/* Summary Cards Section */}
      {payrollData.length > 0 && (
        <div className="payroll-comp__summary">
          <div className="stats-grid">
            <div className="stat-card total-gross">
              <div className="stats-card__accent" aria-hidden="true" />
              <div className="stat-icon">💰</div>
              <div className="stat-content">
                <h3>{formatCurrency(calculateTotals().totalFixedGross)}</h3>
                <p>Total Fixed Gross Salary</p>
              </div>
            </div>
            <div className="stat-card earned-gross">
              <div className="stats-card__accent" aria-hidden="true" />
              <div className="stat-icon">📈</div>
              <div className="stat-content">
                <h3>{formatCurrency(calculateTotals().totalEarnedGross)}</h3>
                <p>Total Earned Gross Salary</p>
              </div>
            </div>
            <div className="stat-card total-deductions">
              <div className="stats-card__accent" aria-hidden="true" />
              <div className="stat-icon">📉</div>
              <div className="stat-content">
                <h3>{formatCurrency(calculateTotals().totalDeductions)}</h3>
                <p>Total Deductions</p>
              </div>
            </div>
            <div className="stat-card total-net">
              <div className="stats-card__accent" aria-hidden="true" />
              <div className="stat-icon">💵</div>
              <div className="stat-content">
                <h3>{formatCurrency(calculateTotals().totalNetPay)}</h3>
                <p>Total Net Pay</p>
              </div>
            </div>
            {/* Total Earned Other Allowances summary card */}
            <div className="stat-card total-other-allowances">
              <div className="stats-card__accent" aria-hidden="true" />
              <div className="stat-icon">🎁</div>
              <div className="stat-content">
                <h3>
                  {formatCurrency(calculateTotals().totalEarnedOtherAllowances)}
                </h3>
                <p>Total Earned Other Allowances</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {payrollData.length > 0 && (
        <div className="general-table-container">
          <table className="payroll-table">
            <thead>
              {/* Main Header Row */}
              <tr>
                <th rowSpan="2" className="sticky-first">
                  <ColoredCheckbox
                    color="green"
                    name="selectAll"
                    label="Select All"
                    checked={
                      selectedIds.length === payrollData.length &&
                      payrollData.length > 0
                    }
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                <th rowSpan="2" className="employee-name">
                  Employee ID
                </th>
                <th rowSpan="2">Period</th>
                <th rowSpan="2">Paid Days</th>
                <th rowSpan="2" className="loss-pay-column">
                  Loss of Pay
                </th>
                <th rowSpan="2">Base Days</th>
                <th rowSpan="2">Fixed Gross Salary</th>
                <th rowSpan="2">Earned Gross Salary</th>
                <th rowSpan="2">Fixed Net Salary</th>

                {/* Fixed Salary Section */}
                {tableStructure &&
                  tableStructure.uniqueFixedEarnings.length > 0 && (
                    <th
                      colSpan={tableStructure.uniqueFixedEarnings.length}
                      className="fixed-earnings-column"
                    >
                      Fixed Salary
                    </th>
                  )}

                {/* Earned Salary Section */}
                {tableStructure &&
                  tableStructure.uniqueEarnedEarnings.length > 0 && (
                    <th
                      colSpan={tableStructure.uniqueEarnedEarnings.length}
                      className="earned-earnings-column"
                    >
                      Earned Salary
                    </th>
                  )}

                {/* Other Earnings (Allowances, Reimbursements) */}
                {tableStructure &&
                  (tableStructure.uniqueAllowances.length > 0 ||
                    tableStructure.uniqueReimbursements.length > 0) && (
                    <th
                      colSpan={
                        tableStructure.uniqueAllowances.length +
                        tableStructure.uniqueReimbursements.length
                      }
                      className="allowance-column"
                    >
                      Allowance
                    </th>
                  )}

                {/* Total Earned Other Allowance */}
                <th rowSpan="2" className="total-other-allowances-column">
                  Total Earned Other Allowance
                </th>

                {/* Deductions Section */}
                {tableStructure &&
                  tableStructure.uniqueLoans.length +
                    tableStructure.uniqueEmployeeContribs.length +
                    tableStructure.uniqueDeductions.length >
                    0 && (
                    <th
                      colSpan={
                        tableStructure.uniqueLoans.length +
                        tableStructure.uniqueEmployeeContribs.length +
                        tableStructure.uniqueDeductions.length
                      }
                      className="deductions-header"
                    >
                      Deductions
                    </th>
                  )}

                <th rowSpan="2" className="total-deductions-column">
                  Total Deductions
                </th>
                <th rowSpan="2" className="net-pay-column">
                  Net Pay
                </th>

                {/* Employer Contributions Section */}
                {tableStructure &&
                  tableStructure.uniqueEmployerContribs.length > 0 && (
                    <th
                      colSpan={tableStructure.uniqueEmployerContribs.length}
                      className="contributions-header"
                    >
                      Employer Contribution
                    </th>
                  )}

                <th rowSpan="2" className="sticky-last">
                  Actions
                </th>
              </tr>

              <tr>
                {/* Fixed Earnings Sub Headers */}
                {tableStructure?.uniqueFixedEarnings.map((earningName) => (
                  <th
                    key={`fixed-${earningName}`}
                    className="fixed-earnings-column"
                  >
                    {earningName}
                  </th>
                ))}

                {/* Earned Earnings Sub Headers */}
                {tableStructure?.uniqueEarnedEarnings.map((earningName) => (
                  <th
                    key={`earned-${earningName}`}
                    className="earned-earnings-column"
                  >
                    {earningName}
                  </th>
                ))}

                {/* Allowances Sub Headers */}
                {tableStructure?.uniqueAllowances.map((allowanceName) => (
                  <th
                    key={`allowance-${allowanceName}`}
                    className="allowance-column"
                  >
                    {allowanceName}
                  </th>
                ))}

                {/* Reimbursements Sub Headers */}
                {tableStructure?.uniqueReimbursements.map((reimbName) => (
                  <th
                    key={`reimb-${reimbName}`}
                    className="reimbursement-column"
                  >
                    {reimbName}
                  </th>
                ))}

                {/* Deductions Sub Headers */}
                {tableStructure?.uniqueLoans.map((loanName) => (
                  <th key={`loan-${loanName}`} className="deductions-header">
                    {loanName}
                  </th>
                ))}
                {tableStructure?.uniqueEmployeeContribs.map((contribName) => (
                  <th
                    key={`emp-contrib-${contribName}`}
                    className="deductions-header"
                  >
                    {contribName}
                  </th>
                ))}
                {tableStructure?.uniqueDeductions.map((deductionName) => (
                  <th
                    key={`deduction-${deductionName}`}
                    className="deductions-header"
                  >
                    {deductionName}
                  </th>
                ))}

                {/* Employer Contributions Sub Headers */}
                {tableStructure?.uniqueEmployerContribs.map((contribName) => (
                  <th
                    key={`employer-contrib-${contribName}`}
                    className="contributions-header"
                  >
                    {contribName}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {transformedData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <td className="sticky-first employee-name">
                    {row.employeeWithSelect}
                  </td>
                  <td className="employee-id">{row.userEmployeeId || "N/A"}</td>
                  <td>{row.period}</td>
                  <td>{row.paidDays}</td>
                  <td className="loss-pay-cell">{row.lossOfPay}</td>
                  <td>{row.baseDays}</td>
                  <td>{row.fixedGrossSalary}</td>
                  <td>{row.earnedGrossSalary}</td>
                  <td>{row.fixedNetSalary}</td>

                  {/* Fixed Earnings Data */}
                  {tableStructure?.uniqueFixedEarnings.map((earningName) => (
                    <td
                      key={`fixed-${earningName}-${rowIndex}`}
                      className="fixed-earnings-column"
                    >
                      {row[`fixedEarning_${earningName}`]}
                    </td>
                  ))}

                  {/* Earned Earnings Data */}
                  {tableStructure?.uniqueEarnedEarnings.map((earningName) => (
                    <td
                      key={`earned-${earningName}-${rowIndex}`}
                      className="earned-earnings-column"
                    >
                      {row[`earnedEarning_${earningName}`]}
                    </td>
                  ))}

                  {/* Allowance Data */}
                  {tableStructure?.uniqueAllowances.map((allowanceName) => (
                    <td
                      key={`allowance-${allowanceName}-${rowIndex}`}
                      className="allowance-cell"
                    >
                      {row[`allowance_${allowanceName}`]}
                    </td>
                  ))}

                  {/* Reimbursement Data */}
                  {tableStructure?.uniqueReimbursements.map((reimbName) => (
                    <td
                      key={`reimb-${reimbName}-${rowIndex}`}
                      className="reimbursement-cell"
                    >
                      {row[`reimbursement_${reimbName}`]}
                    </td>
                  ))}

                  {/* Total Earned Other Allowance Data */}
                  <td className="total-other-allowances-cell">
                    {row.totalEarnedOtherAllowances}
                  </td>

                  {/* Deduction Data */}
                  {tableStructure?.uniqueLoans.map((loanName) => (
                    <td
                      key={`loan-${loanName}-${rowIndex}`}
                      className="deduction-cell"
                    >
                      {row[`loan_${loanName}`]}
                    </td>
                  ))}
                  {tableStructure?.uniqueEmployeeContribs.map((contribName) => (
                    <td
                      key={`emp-contrib-${contribName}-${rowIndex}`}
                      className="deduction-cell"
                    >
                      {row[`empContrib_${contribName}`]}
                    </td>
                  ))}
                  {tableStructure?.uniqueDeductions.map((deductionName) => (
                    <td
                      key={`deduction-${deductionName}-${rowIndex}`}
                      className="deduction-cell"
                    >
                      {row[`deduction_${deductionName}`]}
                    </td>
                  ))}

                  <td className="total-deductions-cell">
                    {row.totalDeductions}
                  </td>
                  <td className="net-pay-cell">{row.netPay}</td>

                  {/* Employer Contributions Data */}
                  {tableStructure?.uniqueEmployerContribs.map((contribName) => (
                    <td
                      key={`employer-contrib-${contribName}-${rowIndex}`}
                      className="contribution-cell"
                    >
                      {row[`emplerContrib_${contribName}`]}
                    </td>
                  ))}

                  <td className="sticky-last">{row.actions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Enhanced Action Bar with ref and visual indicators */}
      {selectedIds.length > 0 && (
        <div ref={actionBarRef} className="action-bar action-bar--highlighted">
          <div className="bulk-actions-buttons">
            <button
              className="action-btn approve-all"
              onClick={() => {
                handleBulkAction("Approve");
              }}
            >
              Approve All ({selectedIds.length})
            </button>
            <button
              className="action-btn reject-all"
              onClick={() => {
                handleBulkAction("Reject");
              }}
            >
              Reject All ({selectedIds.length})
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PayrollComp;
